/**
 * Unified per-room lines for order summary / offer UI (multi-room hotel booking).
 * Source priority: multiRoomProfileSelections (complete) → compositeBookingContext.room_quotes → single-room (null).
 */

import { getRoomOccupancyLabel } from "./multiRoomProfileSelectionModel";
import { isHotelOrderSummaryMultiRoomDebug } from "./hotelSearchDebugFlags";
import {
  getHotelProfilePackageCurrency,
  getHotelProfilePackageSupplierUsd,
} from "./hotelSellingPrice";

/**
 * Dedupe repeated supplier room name tokens (e.g. ["Deluxe","Deluxe"] → "Deluxe").
 * @param {unknown} roomNames
 * @returns {string}
 */
export function formatDedupedRoomTypeLabel(roomNames) {
  if (roomNames == null) return "—";
  if (typeof roomNames === "string") {
    const s = roomNames.trim();
    return s || "—";
  }
  if (!Array.isArray(roomNames)) return "—";
  const unique = [
    ...new Set(
      roomNames
        .map((x) => (x == null ? "" : String(x).trim()))
        .filter(Boolean)
    ),
  ];
  if (unique.length === 0) return "—";
  return unique.join(", ");
}

function boardSnippet(roomBasis) {
  if (!roomBasis || typeof roomBasis !== "string") return "";
  const parts = roomBasis.trim().split(/\s+/);
  return parts.length ? parts[parts.length - 1] : "";
}

/** Wizard draft uses flat `{ selectedRoom, selectedHotelPackage }`; legacy uses `selected_option`. */
export function normalizeMultiRoomProfileSelection(sel) {
  if (!sel || typeof sel !== "object") return null;
  if (sel.selected_option?.selectedHotelPackage) return sel;
  if (sel.selectedHotelPackage) {
    return {
      ...sel,
      selected_option: {
        selectedRoom: sel.selectedRoom ?? null,
        selectedHotelPackage: sel.selectedHotelPackage,
      },
    };
  }
  return sel;
}

export function isMultiRoomProfileSelectionsComplete(mrs) {
  if (!Array.isArray(mrs) || mrs.length < 2) return false;
  return mrs.every((s) =>
    Boolean(normalizeMultiRoomProfileSelection(s)?.selected_option?.selectedHotelPackage)
  );
}

function legFromSelection(sel, displayIndex, t) {
  const norm = normalizeMultiRoomProfileSelection(sel);
  const opt = norm?.selected_option;
  const pkg = opt?.selectedHotelPackage;
  const room = opt?.selectedRoom;
  if (!pkg) return null;

  const rawNames = room?.roomNames ?? pkg.roomNames;
  const roomName = formatDedupedRoomTypeLabel(rawNames);
  const occupancyLabel = getRoomOccupancyLabel(sel?.room_request ?? null, t);
  const basis = pkg.roomBasis || pkg.RoomBasis || "";
  const mealPlan = boardSnippet(basis) || basis || "—";
  const supplier = getHotelProfilePackageSupplierUsd(pkg);
  const legacyFinal = Number(pkg.finalPrice);
  const priceUsd =
    supplier != null
      ? supplier
      : Number.isFinite(legacyFinal) && legacyFinal > 0
        ? legacyFinal
        : null;
  const ourPriceCommissionUsd =
    pkg.ourPrice != null && pkg.ourPrice.priceWithCommission != null
      ? Number(pkg.ourPrice.priceWithCommission)
      : priceUsd;
  const currency =
    getHotelProfilePackageCurrency(pkg) ||
    pkg.ourPrice?.originalCurrency ||
    pkg.currency ||
    "USD";
  const session = String(
    pkg.supplier_session || pkg.session || ""
  ).trim();
  const packageId = String(pkg.packageID ?? pkg.PackageId ?? "").trim();

  const ri = Number(sel?.room_index ?? displayIndex - 1);

  return {
    roomIndex: displayIndex,
    room_index: ri,
    roomName,
    occupancyLabel,
    mealPlan,
    priceUsd: priceUsd != null && Number.isFinite(Number(priceUsd)) ? Number(priceUsd) : null,
    ourPriceCommissionUsd:
      ourPriceCommissionUsd != null && Number.isFinite(Number(ourPriceCommissionUsd))
        ? Number(ourPriceCommissionUsd)
        : null,
    currency,
    session,
    packageId,
    roomBasis: basis,
    roomRequest: sel?.room_request ?? null,
  };
}

function firstGemonixRoomName(q) {
  const rooms = q?.gemonix_rooms || q?.package_row?.Rooms || [];
  if (!Array.isArray(rooms) || !rooms.length) return "";
  const r0 = rooms[0];
  return (
    r0?.RoomName ||
    r0?.Name ||
    r0?.roomName ||
    r0?.room_name ||
    ""
  );
}

function legFromRoomQuote(q, displayIndex, t) {
  const names = [];
  const n0 = firstGemonixRoomName(q);
  if (n0) names.push(n0);
  const roomName = formatDedupedRoomTypeLabel(
    names.length ? names : [q?.package_row?.PackageName || ""]
  );
  const occupancyLabel = getRoomOccupancyLabel(q?.room_request ?? null, t);
  const pkg = q?.package_row || {};
  const basis = pkg.RoomBasis || pkg.roomBasis || "";
  const mealPlan = boardSnippet(basis) || basis || "—";
  const supplier = getHotelProfilePackageSupplierUsd(pkg);
  const fromQuote = Number(
    q?.room_price != null
      ? q.room_price
      : pkg?.PackagePrice?.FinalPrice ?? pkg?.finalPrice
  );
  const priceUsd =
    supplier != null
      ? supplier
      : Number.isFinite(fromQuote) && fromQuote > 0
        ? fromQuote
        : null;
  const packageId = String(q?.package_id ?? "").trim();
  const session = String(q?.supplier_session ?? "").trim();
  const ri = Number(q?.room_index ?? displayIndex - 1);

  return {
    roomIndex: displayIndex,
    room_index: ri,
    roomName,
    occupancyLabel,
    mealPlan,
    priceUsd: priceUsd != null && Number.isFinite(Number(priceUsd)) ? Number(priceUsd) : null,
    ourPriceCommissionUsd: null,
    currency: getHotelProfilePackageCurrency(pkg) || "USD",
    session,
    packageId,
    roomBasis: basis,
    roomRequest: q?.room_request ?? null,
  };
}

function sortByRoomIndex(legs) {
  return legs.slice().sort((a, b) => {
    const ai = Number(a.room_index ?? a.roomIndex ?? 0);
    const bi = Number(b.room_index ?? b.roomIndex ?? 0);
    return ai - bi;
  });
}

/**
 * @param {object|null} currentHotelOrder
 * @param {{ t?: import("i18next").TFunction }} [opts]
 * @returns {{
 *   isMultiRoom: boolean,
 *   source: "multiRoomProfileSelections" | "compositeRoomQuotes" | null,
 *   legs: object[],
 *   totalUsdFinalSum: number,
 *   totalUsdCommissionSum: number | null,
 * }}
 */
export function getHotelSummaryLegs(currentHotelOrder, opts = {}) {
  const t = typeof opts.t === "function" ? opts.t : (k) => k;
  const debug = isHotelOrderSummaryMultiRoomDebug();

  const mrs = currentHotelOrder?.multiRoomProfileSelections;
  const rq = currentHotelOrder?.compositeBookingContext?.room_quotes;

  const selectionsComplete = isMultiRoomProfileSelectionsComplete(mrs);

  let source = null;
  let rawLegs = [];

  if (selectionsComplete) {
    source = "multiRoomProfileSelections";
    rawLegs = mrs
      .map((s, i) => legFromSelection(s, i + 1, t))
      .filter(Boolean);
    if (debug && Array.isArray(rq) && rq.length > 1) {
      const idxSel = new Set(mrs.map((s) => Number(s?.room_index ?? -1)));
      const idxQ = rq.map((q) => Number(q?.room_index ?? -1));
      if (idxQ.some((x) => !idxSel.has(x))) {
        console.warn(
          "[HOTEL_ORDER_SUMMARY_MULTI_DEBUG] room_quotes room_index not aligned with multiRoomProfileSelections"
        );
      }
    }
  } else if (Array.isArray(rq) && rq.length > 1) {
    if (
      debug &&
      Array.isArray(mrs) &&
      mrs.length > 1 &&
      !selectionsComplete
    ) {
      console.warn(
        "[HOTEL_ORDER_SUMMARY_MULTI_DEBUG] multiRoomProfileSelections incomplete — using compositeBookingContext.room_quotes for summary legs"
      );
    }
    source = "compositeRoomQuotes";
    rawLegs = rq
      .map((q, i) => legFromRoomQuote(q, i + 1, t))
      .filter(Boolean);

    if (debug) {
      const idxs = rawLegs.map((l) => l.room_index);
      if (new Set(idxs).size !== idxs.length) {
        console.warn(
          "[HOTEL_ORDER_SUMMARY_MULTI_DEBUG] duplicate room_index in room_quotes",
          idxs
        );
      }
    }
  }

  if (!rawLegs.length || rawLegs.length < 2) {
    return {
      isMultiRoom: false,
      source: null,
      legs: [],
      totalUsdFinalSum: 0,
      totalUsdCommissionSum: null,
    };
  }

  const legs = sortByRoomIndex(rawLegs).map((leg, i) => ({
    ...leg,
    roomIndex: i + 1,
  }));

  let totalUsdFinalSum = 0;
  let totalUsdCommissionSum = 0;
  let anyCommission = false;
  for (const leg of legs) {
    if (leg.priceUsd != null && Number.isFinite(leg.priceUsd)) {
      totalUsdFinalSum += leg.priceUsd;
    }
    if (
      leg.ourPriceCommissionUsd != null &&
      Number.isFinite(leg.ourPriceCommissionUsd)
    ) {
      totalUsdCommissionSum += leg.ourPriceCommissionUsd;
      anyCommission = true;
    }
  }

  return {
    isMultiRoom: true,
    source,
    legs,
    totalUsdFinalSum,
    totalUsdCommissionSum: anyCommission ? totalUsdCommissionSum : null,
  };
}

/**
 * USD "priceWithCommission" base for all legs (for OrderContext.calcOurTotalPrice merge).
 * @param {ReturnType<typeof getHotelSummaryLegs>|null} model
 * @param {(usd: number) => { priceWithCommission?: number }} [calcBaseCommission]
 * @returns {number|null}
 */
export function resolveMultiRoomCommissionUsdTotal(model, calcBaseCommission) {
  if (!model?.isMultiRoom || !model.legs?.length) return null;
  if (
    model.totalUsdCommissionSum != null &&
    Number.isFinite(model.totalUsdCommissionSum)
  ) {
    return model.totalUsdCommissionSum;
  }
  if (typeof calcBaseCommission !== "function") return null;
  return model.legs.reduce((sum, leg) => {
    if (
      leg.ourPriceCommissionUsd != null &&
      Number.isFinite(leg.ourPriceCommissionUsd)
    ) {
      return sum + leg.ourPriceCommissionUsd;
    }
    if (leg.priceUsd != null && Number.isFinite(leg.priceUsd)) {
      const op = calcBaseCommission(leg.priceUsd);
      return (
        sum +
        Number(
          op?.priceWithCommission != null ? op.priceWithCommission : leg.priceUsd
        )
      );
    }
    return sum;
  }, 0);
}
